import React, { useEffect, useState, useMemo } from 'react';
import { api, fmt, fmtDate, MONTHS } from '../utils/api';
import Topbar from '../components/Topbar';
import { Badge, EmptyState, Spinner, SearchInput, useToast, Modal } from '../components/UI';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function Royalties({ user, directorView }) {
  const [royalties, setRoyalties] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [matrixYear, setMatrixYear] = useState(new Date().getFullYear());
  const [selected, setSelected] = useState(null);
  const [showToast, Toast] = useToast();
  const [addPaymentModal, setAddPaymentModal] = useState(false);
  const [newPaymentForm, setNewPaymentForm] = useState({ franchiseId:'', month:'', revenue:'', royalty:'', paymentDate:new Date().toISOString().split('T')[0], paymentMethod:'jotform' });
  const [royaltyYear, setRoyaltyYear] = useState(String(new Date().getFullYear()));
  const [branchFilter, setBranchFilter] = useState('active');
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const isAdmin = user?.role === 'admin' || user?.role === 'hq_support';
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const load = () => {
    Promise.all([api.getRoyalties(), api.getFranchises(directorView)]).then(([r, f]) => {
      setRoyalties(r || []);
      setFranchises(f || []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected && franchises.length > 0) {
      const hash = window.location.hash.replace('#', '');
      const match = hash.match(/^royalties:([a-f0-9-]{36})$/);
      if (match) {
        const found = franchises.find(f => f.id === match[1]);
        if (found) setSelected(found);
      }
    }
  }, [franchises]);

  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Royalties" /><Spinner /></div>;

  if (selected) {
    return (
      <BranchRoyaltyDetail
        franchise={selected}
        royalties={royalties.filter(r => r.franchiseId === selected.id || r.franchise_id === selected.id)}
        allRoyalties={royalties}
        user={user}
        isAdmin={isAdmin}
        onBack={() => { setSelected(null); window.location.hash = 'royalties'; }}
        onReload={load}
        showToast={showToast}
        Toast={Toast}
      />
    );
  }

  // Group royalties by franchise
  const byFranchise = {};
  royalties.forEach(r => {
    const fid = r.franchiseId || r.franchise_id;
    if (!byFranchise[fid]) byFranchise[fid] = [];
    byFranchise[fid].push(r);
  });

  // Find overdue — same logic as old app
  const now = new Date();
  const GLOBAL_START_YR = 2025, GLOBAL_START_MO = 8;
  const overdue = [];
  franchises.filter(f => f.status !== 'closed').forEach(f => {
    let startYr = GLOBAL_START_YR, startMo = GLOBAL_START_MO;
    if (f.royaltyStartMonth) {
      const parts = f.royaltyStartMonth.split(' ');
      const mo = MONTH_NAMES.indexOf(parts[0]);
      const yr = parseInt(parts[1]);
      if (mo >= 0 && yr >= 2024) { startYr = yr; startMo = mo; }
    }
    const startIdx = startYr * 12 + startMo;
    const nowIdx = now.getFullYear() * 12 + now.getMonth();
    for (let idx = startIdx; idx < nowIdx; idx++) {
      const y = Math.floor(idx / 12);
      const m = idx % 12;
      const due = new Date(y, m + 1, 10);
      if (due > now) break;
      const monthStr = MONTH_NAMES[m] + ' ' + y;
      const hasPay = royalties.some(r => (r.franchiseId===f.id||r.franchise_id===f.id) && r.month===monthStr);
      if (!hasPay) overdue.push({ franchise: f, month: monthStr });
    }
  });

  const totalYearRoyalty = royalties.filter(r => (r.month||'').includes(String(now.getFullYear()))).reduce((s,r) => s+(parseFloat(r.royalty)||0), 0);
  const totalRoyalty = royalties.reduce((s,r) => s+(parseFloat(r.royalty)||0), 0);

  const filteredFranchises = franchises.filter(f => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (f.name||'').toLowerCase().includes(q) || (f.location||'').toLowerCase().includes(q);
  });

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="Royalties" subtitle="Track royalty payments across all branches" actions={
        <div style={{ display:'flex', gap:8 }}>
          <a href="https://form.jotform.com/252783975723067" target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ textDecoration:'none' }}>
            ↗ Open JotForm
          </a>
          <button className="btn btn-secondary btn-sm" onClick={() => {
            window.open('https://www.jotform.com/submissions/252783975723067', '_blank');
          }}>⚙ Manage Submissions</button>
          <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
          <button className="btn btn-secondary btn-sm" onClick={async () => {
            showToast('Syncing JotForm...');
            await api.syncJotform().catch(()=>{});
            load(); showToast('Sync complete');
          }}>⟳ Sync JotForm</button>
          {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => setAddPaymentModal(true)}>+ Add Payment</button>}
        </div>
      } />
      {Toast}

      <div style={{ padding:24 }}>
        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
          {[
            { label:`${now.getFullYear()} Royalties`, value:fmt(totalYearRoyalty), color:'var(--green)', icon:'ti-trending-up' },
            { label:'Total Royalties', value:fmt(totalRoyalty), color:'var(--blue)', icon:'ti-coin' },
            { label:'Active Branches', value:franchises.filter(f=>f.status==='active').length, color:'var(--navy)', icon:'ti-building-store' },
            { label:'Overdue', value:overdue.length, color:overdue.length>0?'var(--red)':'var(--green)', icon:'ti-alert-triangle' },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding:'14px 16px', display:'flex', gap:12, alignItems:'center' }}>
              <div style={{ width:40, height:40, borderRadius:'var(--r-md)', background:'var(--blue-bg)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className={`ti ${s.icon}`} style={{ fontSize:18, color:s.color }} />
              </div>
              <div>
                <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{s.label}</div>
                <div style={{ fontSize:20, fontWeight:700, color:s.color }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Overdue alert - expandable */}
        {overdue.length > 0 && (
          <OverdueAlert overdue={overdue} onSelect={setSelected} />
        )}

        {/* Payment Matrix Table */}
        <RoyaltyMatrix
          franchises={franchises}
          royalties={royalties}
          matrixYear={matrixYear}
          setMatrixYear={setMatrixYear}
          onSelect={setSelected}
        />

        {/* Filters */}
        <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ flex:1, maxWidth:280 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search branches..." />
          </div>
          <select value={royaltyYear} onChange={e=>setRoyaltyYear(e.target.value)} style={{ width:'auto', marginBottom:0 }}>
            <option value="all">All Years</option>
            {[...new Set(royalties.map(r=>(r.month||'').split(' ')[1]).filter(Boolean))].sort((a,b)=>b-a).map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <select value={branchFilter} onChange={e=>setBranchFilter(e.target.value)} style={{ width:'auto', marginBottom:0 }}>
            <option value="active">Active only</option>
            <option value="all">All branches</option>
          </select>
        </div>

        {/* Branch list */}
        <div className="card" style={{ overflow:'hidden' }}>
          <table className="table">
            <thead><tr>
              <th>BRANCH</th><th>LOCATION</th>
              <th>{royaltyYear==='all'?'Total':royaltyYear} ROYALTIES</th>
              <th>{royaltyYear==='all'?'Total':royaltyYear} REVENUE</th>
              <th>LAST PAYMENT</th><th>STATUS</th><th>PAYMENTS</th><th></th>
            </tr></thead>
            <tbody>
              {filteredFranchises.filter(f => branchFilter==='all' || f.status==='active').map(f => {
                const fRoy = royalties.filter(r => (r.franchiseId===f.id||r.franchise_id===f.id) && (royaltyYear==='all'||(r.month||'').includes(royaltyYear)));
                const allFRoy = royalties.filter(r => r.franchiseId===f.id||r.franchise_id===f.id);
                const sorted = allFRoy.sort((a,b)=>(b.month||'').localeCompare(a.month||''));
                const last = sorted[0];
                const total = fRoy.reduce((s,r)=>s+(parseFloat(r.royalty)||0),0);
                const revenue = fRoy.reduce((s,r)=>s+(parseFloat(r.revenue)||0),0);
                const overdueCount = overdue.filter(o=>o.franchise.id===f.id).length;
                const statusMap = { active:'green', signed:'blue', potential:'amber', closed:'red' };
                return (
                  <tr key={f.id} style={{ cursor:'pointer' }} onClick={() => { setSelected(f); window.location.hash = 'royalties:' + f.id; }}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:32, height:32, borderRadius:'var(--r-md)', background:'var(--blue-bg)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'var(--navy)' }}>
                          {((f.location||f.name)||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                        </div>
                        <span style={{ fontWeight:500, fontSize:13 }}>{f.location || f.name}</span>
                      </div>
                    </td>
                    <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{f.location ? f.name : '—'}</td>
                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:13, fontWeight:600, color:'var(--green)' }}>{fmt(total)}</td>
                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{revenue>0?fmt(revenue):'—'}</td>
                    <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{last?.month||'No payments'}</td>
                    <td>
                      <Badge type={statusMap[f.status]||'navy'}>{f.status}</Badge>
                    </td>
                    <td>
                      {overdueCount > 0 ? <Badge type="red">{overdueCount} overdue</Badge> : <Badge type="green">Up to date</Badge>}
                    </td>
                    <td><i className="ti ti-chevron-right" style={{ fontSize:16, color:'var(--text-tertiary)' }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Payment Modal */}
      {addPaymentModal && (
        <Modal title="Add Royalty Payment" onClose={() => setAddPaymentModal(false)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setAddPaymentModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={async () => {
              if (!newPaymentForm.franchiseId || !newPaymentForm.month || !newPaymentForm.royalty) { showToast('Please fill all required fields'); return; }
              const f = franchises.find(f => f.id === newPaymentForm.franchiseId);
              await api.createRoyalty({ ...newPaymentForm, franchiseName: f?.name });
              await api.logActivity('Added royalty payment', `${f?.location||f?.name} — ${newPaymentForm.month} — £${newPaymentForm.royalty}`);
              showToast('Payment added'); setAddPaymentModal(false); load();
            }}>Save</button>
          </>}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div><label>BRANCH *</label>
              <select value={newPaymentForm.franchiseId} onChange={e=>setNewPaymentForm(f=>({...f,franchiseId:e.target.value}))}>
                <option value="">Select branch...</option>
                {franchises.filter(f=>f.status!=='closed').map(f=><option key={f.id} value={f.id}>{f.location||f.name}{f.location&&f.location!==f.name?' — '+f.name:''}</option>)}
              </select>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div><label>MONTH *</label><input value={newPaymentForm.month} placeholder="e.g. May 2026" onChange={e=>setNewPaymentForm(f=>({...f,month:e.target.value}))} /></div>
              <div><label>ROYALTY (£) *</label><input type="number" value={newPaymentForm.royalty} onChange={e=>setNewPaymentForm(f=>({...f,royalty:e.target.value}))} /></div>
              <div><label>REVENUE (£)</label><input type="number" value={newPaymentForm.revenue} onChange={e=>setNewPaymentForm(f=>({...f,revenue:e.target.value}))} /></div>
              <div><label>PAYMENT DATE</label><input type="date" value={newPaymentForm.paymentDate} onChange={e=>setNewPaymentForm(f=>({...f,paymentDate:e.target.value}))} /></div>
            </div>
            <div><label>METHOD</label>
              <select value={newPaymentForm.paymentMethod} onChange={e=>setNewPaymentForm(f=>({...f,paymentMethod:e.target.value,source:e.target.value}))}>
                {['jotform','bank_transfer','cash','card'].map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Branch Royalty Detail ─────────────────────────────────────────────────────
function BranchRoyaltyDetail({ franchise: f, royalties, allRoyalties, user, isAdmin, onBack, onReload, showToast, Toast }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ month:'', revenue:'', royalty:'', paymentDate: new Date().toISOString().split('T')[0], source:'jotform', paymentMethod:'jotform' });
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const now = new Date();

  // All months from royalty start to now
  const startParts = (f.royaltyStartMonth||'Sep 2025').split(' ');
  const startYr = parseInt(startParts[1]||2025);
  const startMo = MONTH_NAMES.indexOf(startParts[0]||'Sep');
  const endIdx = now.getFullYear()*12 + now.getMonth();
  const allMonths = [];
  for (let idx = startYr*12+startMo; idx <= endIdx; idx++) {
    const y = Math.floor(idx/12); const m = idx%12;
    const due = new Date(y, m+1, 10);
    const mStr = MONTH_NAMES[m]+' '+y;
    const hasPay = royalties.some(r => r.month === mStr);
    if (due <= now || hasPay) allMonths.push(mStr);
  }

  const yearRoyalties = royalties.filter(r => (r.month||'').includes(String(year)));
  const yearMonths = allMonths.filter(m => m.includes(String(year)));
  const totalYear = yearRoyalties.reduce((s,r) => s+(parseFloat(r.royalty)||0), 0);
  const paidCount = yearMonths.filter(m => royalties.some(r=>r.month===m)).length;
  const unpaidCount = yearMonths.length - paidCount;

  // Trend chart data (last 12 months)
  const trendData = useMemo(() => {
    return allMonths.slice(-12).map(m => {
      const r = royalties.find(r => r.month === m);
      return { month:m, royalty: r ? parseFloat(r.royalty)||0 : 0 };
    });
  }, [royalties, allMonths]);

  async function saveAdd() {
    if (!addForm.month || !addForm.royalty) { showToast('Please fill in month and royalty'); return; }
    await api.createRoyalty({ ...addForm, franchiseId:f.id, franchiseName:f.name });
    await api.logActivity('Added royalty payment', `${f.name} — ${addForm.month} — £${addForm.royalty}`);
    showToast('Royalty added'); setAddModal(false); onReload();
  }

  async function saveEdit() {
    await api.updateRoyalty(editModal.id, editForm);
    await api.logActivity('Edited royalty payment', `${f.name} — ${editModal.month}`);
    showToast('Updated'); setEditModal(null); onReload();
  }

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title={f.location||f.name} subtitle={f.location?f.name:"Royalty History"} actions={
        <div style={{ display:'flex', gap:8 }}>
          <a href={`https://form.jotform.com/252783975723067?franchise_id=${f.id}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ textDecoration:'none' }}>
            ↗ Open JotForm
          </a>
          {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => setAddModal(true)}>+ Add Payment</button>}
          <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        </div>
      } />
      {Toast}

      <div style={{ padding:24 }}>
        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
          {[
            { label:`${year} Total`, value:fmt(totalYear), color:'var(--green)' },
            { label:'Months Paid', value:paidCount, color:'var(--blue)' },
            { label:'Months Unpaid', value:unpaidCount, color:unpaidCount>0?'var(--red)':'var(--green)' },
            { label:'All Time', value:fmt(royalties.reduce((s,r)=>s+(parseFloat(r.royalty)||0),0)), color:'var(--navy)' },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding:'12px 16px' }}>
              <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:4 }}>{s.label}</div>
              <div style={{ fontSize:20, fontWeight:700, color:s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Trend chart */}
        {trendData.length > 1 && (
          <div className="card" style={{ padding:20, marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)', marginBottom:12, letterSpacing:'0.05em' }}>ROYALTY TREND (LAST 12 MONTHS)</div>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v=>`£${Math.round(v/1000)}k`} />
                <Tooltip formatter={(v)=>[fmt(v),'Royalty']} contentStyle={{ fontSize:12, borderRadius:8, border:'1px solid #e2e8f0' }} />
                <Line type="monotone" dataKey="royalty" stroke="var(--navy)" strokeWidth={2.5} dot={{ fill:'var(--navy)', r:4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Year nav + table */}
        <div className="card">
          <div style={{ padding:'12px 18px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
            <div style={{ display:'flex', gap:8 }}>
              <span style={{ fontSize:12, color:'var(--text-tertiary)' }}>{paidCount} paid, {unpaidCount} unpaid</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:0, background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', overflow:'hidden' }}>
              <button onClick={()=>setYear(y=>y-1)} style={{ background:'none', border:'none', borderRight:'1px solid var(--border)', cursor:'pointer', color:'var(--text-secondary)', fontSize:16, padding:'6px 14px', lineHeight:1, display:'flex', alignItems:'center' }}>‹</button>
              <span style={{ fontSize:13, fontWeight:700, color:'var(--navy)', padding:'6px 16px', minWidth:60, textAlign:'center' }}>{year}</span>
              <button onClick={()=>setYear(y=>y+1)} style={{ background:'none', border:'none', borderLeft:'1px solid var(--border)', cursor:'pointer', color:'var(--text-secondary)', fontSize:16, padding:'6px 14px', lineHeight:1, display:'flex', alignItems:'center' }}>›</button>
            </div>
          </div>
          {yearMonths.length === 0 ? <EmptyState icon="ti-coin" message={`No royalty data for ${year}`} /> : (
            <table className="table">
              <thead><tr>
                <th>MONTH</th><th>STATUS</th><th>REVENUE</th><th>ROYALTY</th><th>RATE</th>
                <th>PAYMENT DATE</th><th>METHOD</th>{isAdmin&&<th></th>}
              </tr></thead>
              <tbody>
                {yearMonths.slice().reverse().map(month => {
                  const pay = royalties.find(r => r.month === month);
                  const due = (() => { const p=month.split(' '); const MONTHS=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return new Date(parseInt(p[1]),MONTHS.indexOf(p[0])+1,10); })();
                  const isOverdue = !pay && due <= now;
                  return (
                    <tr key={month}>
                      <td style={{ fontWeight:500 }}>{month}</td>
                      <td>
                        {pay ? <Badge type="green">✓ Paid</Badge> :
                          isOverdue ? <Badge type="red">Overdue</Badge> :
                          <Badge type="amber">Pending</Badge>}
                      </td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{pay?fmt(pay.revenue||0):'—'}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color:pay?'var(--green)':'var(--text-tertiary)' }}>{pay?fmt(pay.royalty):'—'}</td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>
                        {pay && pay.revenue > 0 ? `${((parseFloat(pay.royalty)/parseFloat(pay.revenue))*100).toFixed(1)}%` : '—'}
                      </td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{pay?fmtDate(pay.paymentDate):'—'}</td>
                      <td>
                        {pay?.paymentMethod === 'jotform' || pay?.source === 'jotform' ? <Badge type="blue">JotForm</Badge> :
                         pay?.paymentMethod ? <Badge type="navy">{pay.paymentMethod}</Badge> : '—'}
                      </td>
                      {isAdmin && (
                        <td>
                          {pay ? (
                            <div style={{ display:'flex', gap:4 }}>
                              <button className="btn btn-sm btn-secondary" onClick={() => { setEditModal(pay); setEditForm({ revenue:pay.revenue, royalty:pay.royalty, paymentDate:pay.paymentDate?.split('T')[0], source:pay.source, paymentMethod:pay.paymentMethod }); }}>Edit</button>
                              <button className="btn btn-sm btn-danger" onClick={async () => {
                                if (!window.confirm(`Delete royalty for ${month}?`)) return;
                                await api.deleteRoyalty(pay.id);
                                await api.logActivity('Deleted royalty', `${f.name} — ${month}`);
                                showToast('Deleted'); onReload();
                              }}>Del</button>
                            </div>
                          ) : (
                            <button className="btn btn-sm" style={{ background:'var(--blue-badge-bg)', color:'var(--blue)', border:'none', fontSize:11 }} onClick={() => { setAddModal(true); setAddForm(fm=>({...fm,month})); }}>
                              + Add
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {addModal && (
        <Modal title="Add Royalty Payment" onClose={() => setAddModal(false)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setAddModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveAdd}>Save</button>
          </>}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div><label>MONTH *</label><input value={addForm.month} placeholder="e.g. May 2026" onChange={e=>setAddForm(f=>({...f,month:e.target.value}))} /></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div><label>REVENUE (£)</label><input type="number" value={addForm.revenue} onChange={e=>setAddForm(f=>({...f,revenue:e.target.value}))} /></div>
              <div><label>ROYALTY (£) *</label><input type="number" value={addForm.royalty} onChange={e=>setAddForm(f=>({...f,royalty:e.target.value}))} /></div>
              <div><label>PAYMENT DATE</label><input type="date" value={addForm.paymentDate} onChange={e=>setAddForm(f=>({...f,paymentDate:e.target.value}))} /></div>
              <div><label>METHOD</label>
                <select value={addForm.paymentMethod} onChange={e=>setAddForm(f=>({...f,paymentMethod:e.target.value,source:e.target.value}))}>
                  {['jotform','bank_transfer','cash','card'].map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {editModal && (
        <Modal title={`Edit Royalty — ${editModal.month}`} onClose={() => setEditModal(null)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveEdit}>Save</button>
          </>}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div><label>REVENUE (£)</label><input type="number" value={editForm.revenue||''} onChange={e=>setEditForm(f=>({...f,revenue:e.target.value}))} /></div>
              <div><label>ROYALTY (£)</label><input type="number" value={editForm.royalty||''} onChange={e=>setEditForm(f=>({...f,royalty:e.target.value}))} /></div>
              <div><label>PAYMENT DATE</label><input type="date" value={editForm.paymentDate||''} onChange={e=>setEditForm(f=>({...f,paymentDate:e.target.value}))} /></div>
              <div><label>METHOD</label>
                <select value={editForm.paymentMethod||''} onChange={e=>setEditForm(f=>({...f,paymentMethod:e.target.value,source:e.target.value}))}>
                  {['jotform','bank_transfer','cash','card'].map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Overdue Alert Component ───────────────────────────────────────────────────

// ─── Royalty Payment Matrix ────────────────────────────────────────────────────
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function RoyaltyMatrix({ franchises, royalties, matrixYear, setMatrixYear, onSelect }) {
  const [matrixSearch, setMatrixSearch] = React.useState('');
  const [matrixFilter, setMatrixFilter] = React.useState('all');

  const activeFranchises = franchises.filter(f => f.status !== 'closed').sort((a,b) => {
    const na = (a.location||a.name||'').toLowerCase();
    const nb = (b.location||b.name||'').toLowerCase();
    return na < nb ? -1 : na > nb ? 1 : 0;
  });

  // Calculate royalty start for each franchise
  function getStartMonth(f) {
    if (f.royaltyStartMonth) {
      const parts = f.royaltyStartMonth.split(' ');
      const mo = MONTH_ABBR.indexOf(parts[0]);
      const yr = parseInt(parts[1]);
      if (mo >= 0 && yr >= 2024) return { yr, mo };
    }
    return { yr: 2025, mo: 8 }; // Sep 2025 default
  }

  // Only show months up to last month
  const now = new Date();
  const currentYr = now.getFullYear();
  const currentMo = now.getMonth(); // 0-indexed, current month (not yet due)

  const months = MONTH_ABBR.map((abbr, mo) => ({ abbr, mo })).filter(({ mo }) => {
    if (matrixYear < currentYr) return true;
    if (matrixYear === currentYr) return mo < currentMo; // exclude current month
    return false;
  });

  if (months.length === 0 && matrixYear >= currentYr) {
    // Try previous year
  }
  const filteredFranchises = activeFranchises.filter(f => {
    const matchSearch = !matrixSearch || (f.location||f.name||'').toLowerCase().includes(matrixSearch.toLowerCase());
    if (!matchSearch) return false;
    if (matrixFilter === 'all') return true;
    const { yr: syr, mo: smo } = getStartMonth(f);
    const startIdx = syr * 12 + smo;
    const fRoyalties = royalties.filter(r => r.franchiseId===f.id||r.franchise_id===f.id);
    const hasOverdue = months.some(({ mo }) => {
      const mIdx = matrixYear * 12 + mo;
      if (mIdx < startIdx) return false;
      const monthStr = MONTH_ABBR[mo] + ' ' + matrixYear;
      return !fRoyalties.some(r => r.month === monthStr) && new Date(matrixYear, mo+1, 10) < new Date();
    });
    if (matrixFilter === 'overdue') return hasOverdue;
    if (matrixFilter === 'paid') return !hasOverdue;
    return true;
  });

  const colTotals = months.map(({ abbr, mo }) => {
    const monthStr = abbr + ' ' + matrixYear;
    return filteredFranchises.filter(f => {
      const { yr: syr, mo: smo } = getStartMonth(f);
      const startIdx = syr * 12 + smo;
      const mIdx = matrixYear * 12 + mo;
      return mIdx >= startIdx;
    }).filter(f => royalties.some(r => (r.franchiseId===f.id||r.franchise_id===f.id) && r.month===monthStr)).length;
  });

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
        <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)', whiteSpace:'nowrap' }}>Royalty Payment Overview</span>
        <div style={{ display:'flex', gap:8, alignItems:'center', flex:1, justifyContent:'flex-end', flexWrap:'wrap' }}>
          <div style={{ position:'relative', flexShrink:0 }}>
            <i className="ti ti-search" style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', fontSize:12, color:'var(--text-tertiary)', pointerEvents:'none' }} />
            <input
              value={matrixSearch}
              onChange={e => setMatrixSearch(e.target.value)}
              placeholder="Search branches..."
              style={{ fontSize:12, padding:'5px 8px 5px 26px', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', width:160, outline:'none', background:'white' }}
            />
          </div>
          <div style={{ display:'flex', gap:4, alignItems:'center', background:'var(--surface-1)', borderRadius:'var(--r-sm)', border:'1px solid var(--border)', overflow:'hidden', flexShrink:0 }}>
            {[['all','All'],['overdue','Missing'],['paid','Paid']].map(([val, label]) => (
              <button key={val} onClick={() => setMatrixFilter(val)}
                style={{ padding:'5px 10px', fontSize:11, fontWeight:500, border:'none', cursor:'pointer', whiteSpace:'nowrap',
                  background: matrixFilter===val ? 'var(--navy)' : 'transparent',
                  color: matrixFilter===val ? 'white' : 'var(--text-secondary)',
                  borderRight: val !== 'paid' ? '1px solid var(--border)' : 'none' }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ display:'flex', gap:2, alignItems:'center', background:'var(--surface-1)', borderRadius:'var(--r-sm)', border:'1px solid var(--border)', padding:'2px 4px', flexShrink:0 }}>
            <button onClick={() => setMatrixYear(y=>y-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:16, lineHeight:1, padding:'2px 4px' }}>‹</button>
            <span style={{ fontSize:12, fontWeight:600, minWidth:36, textAlign:'center', color:'var(--navy)' }}>{matrixYear}</span>
            <button onClick={() => setMatrixYear(y=>y+1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:16, lineHeight:1, padding:'2px 4px' }}>›</button>
          </div>
        </div>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ background:'#1c1960' }}>
              <th style={{ padding:'8px 12px', textAlign:'left', fontWeight:600, color:'#ffffff', fontSize:11, whiteSpace:'nowrap', minWidth:140, position:'sticky', left:0, background:'#1c1960', zIndex:1 }}>BRANCH</th>
              <th style={{ padding:'8px 8px', textAlign:'center', fontWeight:600, color:'#ffffff', fontSize:11, whiteSpace:'nowrap', minWidth:60 }}>TOTAL</th>
              {months.map(({ abbr }) => (
                <th key={abbr} style={{ padding:'8px 8px', textAlign:'center', fontWeight:600, color:'#ffffff', fontSize:11, minWidth:44 }}>{abbr}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredFranchises.map((f, fi) => {
              const { yr: syr, mo: smo } = getStartMonth(f);
              const startIdx = syr * 12 + smo;
              const fRoyalties = royalties.filter(r => r.franchiseId===f.id||r.franchise_id===f.id);
              const yearTotal = fRoyalties.filter(r=>(r.month||'').includes(String(matrixYear))).reduce((s,r)=>s+(parseFloat(r.royalty)||0),0);

              return (
                <tr key={f.id} style={{ borderBottom:'1px solid var(--border)', background:fi%2===0?'#ffffff':'#f0f5ff', cursor:'pointer' }}
                  onClick={() => onSelect(f)}>
                  <td style={{ padding:'8px 12px', fontWeight:500, color:'var(--navy)', whiteSpace:'nowrap', position:'sticky', left:0, background:fi%2===0?'#ffffff':'#f0f5ff', zIndex:1 }}>
                    {f.location || f.name}
                  </td>
                  <td style={{ padding:'8px 8px', textAlign:'center', fontWeight:700, color:'var(--navy)', fontFamily:'DM Mono,monospace', fontSize:11 }}>
                    {yearTotal > 0 ? '£'+yearTotal.toLocaleString('en-GB',{minimumFractionDigits:0,maximumFractionDigits:0}) : '—'}
                  </td>
                  {months.map(({ abbr, mo }) => {
                    const monthStr = abbr + ' ' + matrixYear;
                    const mIdx = matrixYear * 12 + mo;
                    const active = mIdx >= startIdx;
                    if (!active) return <td key={abbr} style={{ padding:'8px 8px', textAlign:'center', color:'var(--text-tertiary)' }}>—</td>;
                    const paid = fRoyalties.some(r => r.month === monthStr);
                    const isOverdue = !paid && new Date(matrixYear, mo+1, 10) < new Date();
                    return (
                      <td key={abbr} style={{ padding:'8px 8px', textAlign:'center' }}>
                        {paid
                          ? <span style={{ color:'var(--green)', fontSize:14 }}>✓</span>
                          : isOverdue
                            ? <span style={{ color:'var(--red)', fontSize:14, fontWeight:700 }}>!</span>
                            : <span style={{ color:'var(--text-tertiary)' }}>—</span>
                        }
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Totals row */}
            <tr className="total-row" style={{ background:'#1c1960', fontWeight:700 }}>
              <td style={{ padding:'8px 12px', fontSize:11, color:'#ffffff', position:'sticky', left:0, background:'#1c1960', zIndex:1 }}>PAID</td>
              <td style={{ padding:'8px 8px', textAlign:'center', fontSize:11, color:'#ffffff' }}></td>
              {months.map(({ abbr }, mi) => (
                <td key={abbr} style={{ padding:'8px 8px', textAlign:'center', fontSize:11, color:'#ffffff', fontWeight:700 }}>
                  {colTotals[mi]}/{activeFranchises.filter(f => {
                    const { yr: syr, mo: smo } = getStartMonth(f);
                    return (matrixYear * 12 + months[mi].mo) >= (syr * 12 + smo);
                  }).length}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OverdueAlert({ overdue, onSelect }) {
  const [expanded, setExpanded] = useState(false);
  const grouped = {};
  overdue.forEach(o => {
    if (!grouped[o.franchise.id]) grouped[o.franchise.id] = { franchise:o.franchise, months:[] };
    grouped[o.franchise.id].months.push(o.month);
  });
  const groups = Object.values(grouped);

  return (
    <div style={{ border:'1px solid rgba(163,45,45,0.2)', borderRadius:'var(--r-md)', marginBottom:16, overflow:'hidden' }}>
      <div style={{ padding:'12px 16px', background:'rgba(163,45,45,0.04)', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }} onClick={() => setExpanded(e=>!e)}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <i className="ti ti-alert-triangle" style={{ fontSize:16, color:'var(--red)' }} />
          <strong style={{ color:'var(--red)', fontSize:13 }}>⚠ {overdue.length} outstanding payment{overdue.length>1?'s':''} across {groups.length} branch{groups.length>1?'es':''}</strong>
        </div>
        <i className={`ti ti-chevron-${expanded?'up':'down'}`} style={{ fontSize:14, color:'var(--red)' }} />
      </div>
      {expanded && (
        <div style={{ borderTop:'1px solid rgba(163,45,45,0.1)' }}>
          {groups.map((g,i) => (
            <div key={g.franchise.id} style={{ padding:'10px 16px', borderBottom:i<groups.length-1?'1px solid rgba(163,45,45,0.08)':undefined, display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }}
              onClick={() => onSelect(g.franchise)}>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>
                  {g.franchise.location || g.franchise.name}
                  {g.franchise.location && g.franchise.location!==g.franchise.name ? <span style={{ fontSize:11, fontWeight:400, color:'var(--text-tertiary)', marginLeft:6 }}>({g.franchise.name})</span> : null}
                </div>
                <div style={{ fontSize:11, color:'var(--red)', marginTop:2 }}>
                  {g.months.sort((a,b)=>{
                    const MTHS=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                    const pm=a.split(' '),qm=b.split(' ');
                    return (parseInt(qm[1])*12+MTHS.indexOf(qm[0]))-(parseInt(pm[1])*12+MTHS.indexOf(pm[0]));
                  }).join(', ')}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:12, fontWeight:700, color:'var(--red)' }}>{g.months.length} missing</span>
                <i className="ti ti-chevron-right" style={{ fontSize:13, color:'var(--text-tertiary)' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
